<template>
  <div class="reset-shell">
    <div class="reset-card">
      <h1 class="reset-title">Nova senha</h1>

      <div v-if="!token" class="invalid-state">
        <p class="invalid-text">Link inválido. Verifique o e-mail ou solicite um novo link.</p>
        <button class="btn-primary" type="button" @click="router.push('/forgot')">Solicitar novo link</button>
      </div>

      <div v-else-if="done" class="success-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1f8a45" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        <p class="success-text">Senha atualizada com sucesso!</p>
        <button class="btn-primary" type="button" @click="router.push('/')">Ir para login</button>
      </div>

      <form v-else class="reset-form" @submit.prevent="handleSubmit">
        <p class="reset-desc">Escolha uma nova senha para sua conta. Mínimo de 8 caracteres com pelo menos um número.</p>

        <div class="input-field">
          <label for="rs-password">Nova senha</label>
          <input
            id="rs-password"
            v-model="newPassword"
            type="password"
            placeholder="••••••••"
            autocomplete="new-password"
          />
        </div>

        <div class="input-field">
          <label for="rs-confirm">Confirmar senha</label>
          <input
            id="rs-confirm"
            v-model="confirmPassword"
            type="password"
            placeholder="••••••••"
            autocomplete="new-password"
          />
        </div>

        <p v-if="error" class="error-text">{{ error }}</p>

        <button class="btn-primary" type="submit" :disabled="loading">
          {{ loading ? 'Salvando...' : 'Definir nova senha' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '@/store/api'

export default defineComponent({
  name: 'ResetPasswordView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const token = ref('')
    const newPassword = ref('')
    const confirmPassword = ref('')
    const error = ref('')
    const loading = ref(false)
    const done = ref(false)

    onMounted(() => {
      token.value = String(route.query.token || '').trim()
    })

    const handleSubmit = async () => {
      error.value = ''
      if (!newPassword.value || !confirmPassword.value) {
        error.value = 'Preencha os dois campos.'
        return
      }
      if (newPassword.value.length < 8) {
        error.value = 'Senha deve ter pelo menos 8 caracteres.'
        return
      }
      if (!/\d/.test(newPassword.value)) {
        error.value = 'Senha deve conter pelo menos um número.'
        return
      }
      if (newPassword.value !== confirmPassword.value) {
        error.value = 'As senhas não coincidem.'
        return
      }
      loading.value = true
      try {
        await apiFetch('/reset-password', {
          method: 'POST',
          body: JSON.stringify({ token: token.value, newPassword: newPassword.value })
        }, false)
        done.value = true
      } catch (err: unknown) {
        error.value = err instanceof Error ? err.message : 'Erro ao redefinir senha.'
      } finally {
        loading.value = false
      }
    }

    return { token, newPassword, confirmPassword, error, loading, done, handleSubmit, router }
  }
})
</script>

<style scoped lang="scss">
.reset-shell {
  min-height: 100vh;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  background: #f5f2ee;
  padding: 24px;
}

.reset-card {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(31, 30, 26, 0.1);
  padding: 36px 36px 40px;
  display: grid;
  gap: 20px;
}

.reset-title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #1f1e1a;
}

.reset-desc {
  margin: 0;
  font-size: 14px;
  color: #5a5752;
  line-height: 1.6;
}

.reset-form {
  display: grid;
  gap: 16px;
}

.input-field {
  display: grid;
  gap: 6px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: #1f1e1a;
  }

  input {
    width: 100%;
    padding: 10px 12px;
    border: 1.5px solid rgba(31, 30, 26, 0.2);
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.15s;
    box-sizing: border-box;

    &:focus { border-color: #1f1e1a; }
  }
}

.error-text {
  margin: 0;
  font-size: 13px;
  color: #a54b2a;
}

.btn-primary {
  padding: 12px;
  background: #1f1e1a;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.invalid-state,
.success-state {
  display: grid;
  gap: 16px;
  justify-items: center;
  text-align: center;
}

.invalid-text,
.success-text {
  margin: 0;
  font-size: 15px;
  color: #2c2b27;
  line-height: 1.6;
}

@media (max-width: 480px) {
  .reset-card { padding: 28px 22px 32px; }
}
</style>
